import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner} from '@fortawesome/pro-solid-svg-icons';

import {ErrorBanner} from '../../../shared/components';
import {CustomerChipsSelector} from '../components';

interface IPrimaryCustomerOption {
  id: number | string;
  name: string;
}

export interface IPrimaryCustomersSubStepProps {
  isLoadingPrimaryCustomers: boolean;
  totalPrimaryCustomerSelections: number;
  primaryCustomerDeleteQueueLength: number;
  sortedPrimaryCustomers: IPrimaryCustomerOption[];
  expandedPrimaryCustomerId: number | null;
  primaryCustomerSelectionMap: Map<number, Set<number>>;
  sortedPrimaryCustomerCrops: IPrimaryCustomerOption[];
  isSavingPrimaryCustomers: boolean;
  primaryCustomersError: string | null;
  hasApplicationId: boolean;
  onTogglePrimaryCustomer: (customerId: number) => void;
  onSelectPrimaryCustomerCrop: (customerId: number, cropId: number) => void;
  onDeselectPrimaryCustomerCrop: (customerId: number, cropId: number) => void;
  isPrimaryCustomerItemQueuedForDelete: (
    customerId: number,
    cropId: number,
  ) => boolean;
}

export const PrimaryCustomersSubStep: React.FC<
  IPrimaryCustomersSubStepProps
> = ({
  isLoadingPrimaryCustomers,
  totalPrimaryCustomerSelections,
  primaryCustomerDeleteQueueLength,
  sortedPrimaryCustomers,
  expandedPrimaryCustomerId,
  primaryCustomerSelectionMap,
  sortedPrimaryCustomerCrops,
  isSavingPrimaryCustomers,
  primaryCustomersError,
  hasApplicationId,
  onTogglePrimaryCustomer,
  onSelectPrimaryCustomerCrop,
  onDeselectPrimaryCustomerCrop,
  isPrimaryCustomerItemQueuedForDelete,
}) => (
  <div className="primary-customers-step">
    <header className="primary-customers-step__intro">
      <h3>Who are your primary customers?</h3>
      <p>
        Select your customers and choose the products they typically buy from
        your farm.
      </p>
    </header>

    <div className="primary-customers-step__status" aria-live="polite">
      {isLoadingPrimaryCustomers ? (
        <span className="primary-customers-step__status-message">
          <FontAwesomeIcon icon={faSpinner} spin role="status" />
          Loading customers...
        </span>
      ) : (
        <span>
          {totalPrimaryCustomerSelections > 0
            ? `${totalPrimaryCustomerSelections} ${
                totalPrimaryCustomerSelections === 1
                  ? 'selection'
                  : 'selections'
              } captured`
            : 'No customer selections captured yet'}
        </span>
      )}

      {primaryCustomerDeleteQueueLength > 0 ? (
        <span className="primary-customers-step__status-queued">
          {primaryCustomerDeleteQueueLength}{' '}
          {primaryCustomerDeleteQueueLength === 1 ? 'item' : 'items'} queued for
          deletion
        </span>
      ) : null}
    </div>

    <section
      className="primary-customers-step__list"
      aria-live="polite"
      aria-busy={isLoadingPrimaryCustomers}
    >
      {sortedPrimaryCustomers.length === 0 && !isLoadingPrimaryCustomers ? (
        <p className="primary-customers-step__empty">
          We could not find any customers linked to your profile.
        </p>
      ) : null}

      {sortedPrimaryCustomers.map((customer) => {
        const customerId = Number(customer.id);
        const selectedSet = primaryCustomerSelectionMap.get(customerId);
        const selectedCount = selectedSet ? selectedSet.size : 0;
        const expanded = expandedPrimaryCustomerId === customerId;

        return (
          <CustomerChipsSelector
            key={customer.id}
            customer={customer}
            expanded={expanded}
            selectedCount={selectedCount}
            selectedSet={selectedSet}
            crops={sortedPrimaryCustomerCrops}
            isSaving={isSavingPrimaryCustomers}
            onToggle={onTogglePrimaryCustomer}
            onSelect={onSelectPrimaryCustomerCrop}
            onDeselect={onDeselectPrimaryCustomerCrop}
            isQueuedForDelete={isPrimaryCustomerItemQueuedForDelete}
          />
        );
      })}
    </section>

    <ErrorBanner message={primaryCustomersError} />

    {!hasApplicationId ? (
      <ErrorBanner
        message={
          'We could not determine your application. Please return to the previous step and try again.'
        }
      />
    ) : null}
  </div>
);
